import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UndoIcon from '@mui/icons-material/Undo';

import { parseFastApiFormErrors } from '../../../services/apiErrors';
import { useAuth } from '../../../contexts/AuthContext';
import { useBotStores, useIntegrationConfig, useIntegrations, useSaveIntegrationConfig } from '../../../hooks/useIntegrations';
import BoundedPageLayout from '../../../components/dashboard/BoundedPageLayout';
import PageHeader from '../../../components/dashboard/PageHeader';
import LoadingState from '../../../components/dashboard/LoadingState';
import ErrorState from '../../../components/dashboard/ErrorState';
import PermissionDenied from '../../../components/dashboard/PermissionDenied';
import IntegrationLogo from './IntegrationLogo';
import StoreChips from './StoreChips';
import { customerConfig, needsManualSetup, slotTakenBy } from './integrationStatus';
import {
  HOURS,
  PROVIDERS,
  buildPayload,
  fieldLabelKey,
  initialValues,
  isClearable,
  optionLabelKey,
  validate,
} from './providers';

// HTTPException answers of /crm-configs/ and /stock-configs/ that have no field to attach to.
const SAVE_ERROR_KEYS = {
  403: 'integrations.form.errors.forbidden',
  409: 'integrations.form.errors.duplicate',
};

function SwitchRow({ label, hint, checked, onChange, divider }) {
  return (
    <ListItem
      disableGutters
      divider={divider}
      secondaryAction={<Switch edge="end" checked={checked} onChange={(event) => onChange(event.target.checked)} slotProps={{ input: { 'aria-label': label } }} />}
    >
      <ListItemText primary={label} secondary={hint} />
    </ListItem>
  );
}

export default function IntegrationForm({ isEditMode = false }) {
  const { type, provider } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { customer } = useAuth();
  const creating = !isEditMode;
  const spec = PROVIDERS[provider];

  const integrationsQuery = useIntegrations();
  const integration = integrationsQuery.data?.find((item) => item.type === type && item.key === provider);
  const summary = customerConfig(integration);
  const configQuery = useIntegrationConfig(type, isEditMode ? summary?.id : undefined);
  const storesQuery = useBotStores(type === 'stock' ? customer?.uuid : undefined);

  const initial = useMemo(
    () => (spec && (creating || configQuery.data) ? initialValues(type, provider, configQuery.data) : null),
    [spec, creating, configQuery.data, type, provider],
  );
  const [values, setValues] = useState(null);
  const [blurred, setBlurred] = useState({});
  const [touched, setTouched] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (initial) setValues(initial);
  }, [initial]);

  const takenBy = creating ? slotTakenBy(integrationsQuery.data, integration ?? { type, key: provider }) : null;
  const errors = values ? validate(type, provider, values, { creating }) : {};
  const isValid = Object.keys(errors).length === 0 && !takenBy;
  const detailPath = `/integrations/${type}/${provider}`;

  const mutation = useSaveIntegrationConfig(type, creating ? undefined : summary?.id, {
    onSuccess: () => {
      if (creating) {
        navigate('/integrations', { state: { flashMessage: t('integrations.saveSuccessCreate', { name: integration.name }) } });
        return;
      }
      navigate(detailPath);
    },
    onError: (error) => {
      const status = error?.response?.status;
      if (status === 422) {
        const parsed = parseFastApiFormErrors(error, Object.keys(values), (item) =>
          (item.type === 'missing' ? t('integrations.validation.required') : item.msg));
        setFieldErrors(parsed.fieldErrors);
        setFormError(parsed.formError || (Object.keys(parsed.fieldErrors).length ? '' : t('integrations.saveError')));
        return;
      }
      setFormError(t(SAVE_ERROR_KEYS[status] ?? 'integrations.saveError'));
    },
  });

  const name = integration?.name ?? '';
  const header = (
    <PageHeader
      icon={integration && <IntegrationLogo integration={integration} size={32} />}
      title={t(creating ? 'integrations.createTitle' : 'integrations.editTitle', { name })}
      breadcrumbs={[
        { label: t('integrations.breadcrumb.dashboard') },
        { label: t('nav.groups.settings') },
        { label: t('integrations.breadcrumb.list'), to: '/integrations' },
        ...(creating ? [] : [{ label: name, to: detailPath }]),
        { label: t(creating ? 'integrations.breadcrumb.new' : 'integrations.breadcrumb.edit') },
      ]}
    />
  );
  const page = (body) => <BoundedPageLayout header={header}>{body}</BoundedPageLayout>;

  if (integrationsQuery.isLoading || (isEditMode && configQuery.isLoading)) {
    return page(<LoadingState message={t('integrations.states.loading')} />);
  }
  const failed = [integrationsQuery, configQuery].find((query) => query.isError);
  if (failed) {
    if (failed.error?.response?.status === 403) {
      return page(
        <PermissionDenied
          title={t('integrations.states.permissionDeniedTitle')}
          message={t('integrations.states.permissionDeniedMessage')}
        />,
      );
    }
    return page(
      <ErrorState
        title={t('integrations.states.errorTitle')}
        message={t('integrations.states.errorMessage')}
        actionLabel={t('integrations.states.retry')}
        onRetry={failed.refetch}
      />,
    );
  }
  if (!integration || !spec) {
    return page(<ErrorState title={t('integrations.states.notFoundTitle')} message={t('integrations.states.notFoundMessage')} />);
  }
  // One row per customer: create and edit swap into each other when the URL asks for the wrong one.
  if (creating && summary) return <Navigate to={`${detailPath}/edit`} replace />;
  if (isEditMode && !summary) return <Navigate to={`${detailPath}/new`} replace />;
  if (!values) return page(<LoadingState message={t('integrations.states.loading')} />);

  const setValue = (field, value) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const shownError = (field) => {
    const error = (touched || blurred[field]) && errors[field];
    if (error) return t(`integrations.validation.${error.key}`, error.params);
    return fieldErrors[field];
  };

  const optional = (label) => t('integrations.form.optional', { label });

  // Clearable credentials: null = remove the stored value on save (see isClearable).
  const clearAction = (field) => {
    const cleared = values[field.name] === null;
    const label = t(cleared ? 'integrations.actions.restoreStored' : 'integrations.actions.removeStored');
    return (
      <InputAdornment position="end">
        <IconButton edge="end" aria-label={label} title={label} onClick={() => setValue(field.name, cleared ? '' : null)}>
          {cleared ? <UndoIcon /> : <DeleteOutlineIcon />}
        </IconButton>
      </InputAdornment>
    );
  };

  const renderField = (field) => {
    const labelKey = fieldLabelKey(provider, field.name);
    const label = t(labelKey);
    const error = shownError(field.name);
    const isCredential = field.name.startsWith('credentials.');
    const clearable = isClearable(field, creating);
    const cleared = values[field.name] === null;
    const kindHint = ['list', 'intList'].includes(field.kind) ? t('integrations.form.listHint') : ' ';
    let hint = t(`${labelKey}Helper`, { defaultValue: kindHint });
    if (isCredential && !creating) hint = t(cleared ? 'integrations.form.removedOnSave' : 'integrations.form.keepStored');
    const options = field.kind === 'optionalBool' ? ['', 'true', 'false'] : field.options;
    const optionLabel = (option) => {
      if (field.kind !== 'optionalBool') return t(optionLabelKey(provider, field.name, option));
      return t({ '': 'integrations.values.providerDefault', true: 'integrations.values.yes', false: 'integrations.values.no' }[option]);
    };
    return (
      <TextField
        key={field.name}
        select={Boolean(options)}
        type={{ secret: 'password', email: 'email' }[field.kind] ?? 'text'}
        label={field.required ? label : optional(label)}
        required={field.required && (creating || !isCredential)}
        value={values[field.name] ?? ''}
        onChange={(event) => setValue(field.name, event.target.value)}
        onBlur={() => setBlurred((prev) => ({ ...prev, [field.name]: true }))}
        error={Boolean(error)}
        helperText={error || hint}
        disabled={cleared}
        autoComplete={field.kind === 'secret' ? 'new-password' : 'off'}
        slotProps={{
          htmlInput: field.kind === 'int' ? { inputMode: 'numeric' } : undefined,
          input: clearable ? { endAdornment: clearAction(field) } : undefined,
          inputLabel: field.kind === 'optionalBool' ? { shrink: true } : undefined,
          select: field.kind === 'optionalBool' ? { displayEmpty: true } : undefined,
        }}
        fullWidth
      >
        {options?.map((option) => (
          <MenuItem key={option} value={option}>
            {optionLabel(option)}
          </MenuItem>
        ))}
      </TextField>
    );
  };

  const textField = (field, props) => {
    const error = shownError(field);
    const label = t(`integrations.fields.${field}`);
    return (
      <TextField
        label={props.required ? label : optional(label)}
        value={values[field]}
        onChange={(event) => setValue(field, event.target.value)}
        onBlur={() => setBlurred((prev) => ({ ...prev, [field]: true }))}
        error={Boolean(error)}
        helperText={error || t(`integrations.fields.${field}Helper`)}
        fullWidth
        {...props}
      />
    );
  };

  const hourSelect = (field) => {
    const error = shownError(field);
    return (
      <TextField
        select
        label={optional(t(`integrations.fields.${field}`))}
        value={values[field]}
        onChange={(event) => setValue(field, event.target.value)}
        error={Boolean(error)}
        helperText={error || ' '}
        fullWidth
      >
        <MenuItem value="">{t('integrations.values.anyTime')}</MenuItem>
        {HOURS.map((hour) => (
          <MenuItem key={hour} value={hour}>{`${hour.padStart(2, '0')}:00`}</MenuItem>
        ))}
      </TextField>
    );
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setTouched(true);
    setFieldErrors({});
    setFormError('');
    if (!isValid) return;
    mutation.mutate(buildPayload(type, provider, values, { creating, initial, stored: configQuery.data?.settings }));
  };

  const credentials = spec.credentials.filter((field) => !field.group);
  const oauth = spec.credentials.filter((field) => field.group === 'oauth');
  // One option per agent, named by all its stores; a stored bot_id no store has anymore stays selectable as itself.
  const bots = storesQuery.data ?? [];
  const orphanBot = values.bot_id && !bots.some((bot) => bot.botId === values.bot_id);
  const orphanLabel = storesQuery.isSuccess ? t('integrations.values.orphanBot', { id: values.bot_id }) : values.bot_id;
  const renderBot = (botId) => {
    const bot = bots.find((item) => item.botId === botId);
    return bot ? <StoreChips stores={bot.stores} /> : orphanLabel;
  };
  const crmFlags = ['export_on_lead_created', 'export_on_lead_expired'];

  return page(
    <Stack component="form" noValidate onSubmit={handleSubmit} spacing={3} width="100%" pb={3}>
      {needsManualSetup(integration) && <Alert severity="info">{t(`integrations.providers.${provider}.manualSetupHint`)}</Alert>}
      <Card>
        <CardHeader
          title={t('integrations.form.connection')}
          subheader={t(creating ? 'integrations.form.connectionHint' : 'integrations.form.connectionHintEdit', { name })}
          titleTypographyProps={{ variant: 'h6' }}
        />
        <Divider />
        <CardContent>
          <Stack spacing={2.5}>
            {textField('api_url', { type: 'url', required: true })}
            {type === 'stock' && textField('bot_id', {
              select: true,
              required: true,
              disabled: storesQuery.isLoading,
              slotProps: { select: { renderValue: renderBot } },
              ...(storesQuery.isSuccess && bots.length === 0 && { helperText: shownError('bot_id') || t('integrations.fields.bot_idEmpty') }),
              children: [
                ...bots.map((bot) => <MenuItem key={bot.botId} value={bot.botId}><StoreChips stores={bot.stores} /></MenuItem>),
                orphanBot && <MenuItem key="orphan" value={values.bot_id}>{orphanLabel}</MenuItem>,
              ],
            })}
            {credentials.map(renderField)}
            {oauth.length > 0 && (
              <Stack spacing={0.5}>
                <Typography variant="overline" color="text.secondary">
                  {t(`integrations.providers.${provider}.oauthTitle`)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t(`integrations.providers.${provider}.oauthHint`)}
                </Typography>
              </Stack>
            )}
            {oauth.map(renderField)}
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardHeader
          title={t('integrations.form.settings', { name })}
          subheader={t(`integrations.providers.${provider}.settingsHint`)}
          titleTypographyProps={{ variant: 'h6' }}
        />
        <Divider />
        <CardContent>
          <Stack spacing={2.5}>
            {spec.settings.map(renderField)}
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardHeader
          title={t('integrations.form.behavior')}
          subheader={t(`integrations.form.behaviorHint.${type}`)}
          titleTypographyProps={{ variant: 'h6' }}
        />
        <Divider />
        <CardContent>
          <Stack spacing={2.5}>
            <List disablePadding>
              <SwitchRow
                label={t('integrations.fields.enabled')}
                hint={t('integrations.fields.enabledHelper')}
                checked={values.enabled}
                onChange={(checked) => setValue('enabled', checked)}
                divider={type === 'crm'}
              />
              {type === 'crm' && crmFlags.map((field, index) => (
                <SwitchRow
                  key={field}
                  label={t(`integrations.fields.${field}`)}
                  hint={t(`integrations.fields.${field}Helper`)}
                  checked={values[field]}
                  onChange={(checked) => setValue(field, checked)}
                  divider={index < crmFlags.length - 1}
                />
              ))}
            </List>
            {type === 'stock' && (
              <Stack spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  {t('integrations.fields.syncWindowHelper')}
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  {hourSelect('sync_window_start')}
                  {hourSelect('sync_window_end')}
                </Stack>
              </Stack>
            )}
            {takenBy && (
              <Alert severity="warning">
                {t('integrations.form.slotTaken', { name: takenBy.name, type: t(`integrations.types.${type}`) })}
              </Alert>
            )}
            {formError && <Alert severity="error">{formError}</Alert>}
          </Stack>
        </CardContent>
        <Divider />
        <CardActions sx={{ px: 3, py: 2, gap: 1, justifyContent: 'flex-end' }}>
          <Button type="button" color="inherit" onClick={() => navigate(-1)}>
            {t('integrations.actions.cancel')}
          </Button>
          <Button type="submit" variant="contained" disabled={!isValid || mutation.isPending}>
            {t(creating ? 'integrations.actions.saveCreate' : 'integrations.actions.saveEdit')}
          </Button>
        </CardActions>
      </Card>
    </Stack>,
  );
}
